<template>
  <div
    class="waper-opinion-item"
    @click="() => navigateTo({ path: opinion.route })"
  >
    <div v-if="isTabletLandscape || isDesktop">
      <div class="col img-container">
        <NuxtImg
          v-if="opinion.user?.avatar?.url"
          :src="opinion.user.avatar.url"
          :alt="opinion.user.nickname"
          loading="lazy"
          quality="80"
          width="336"
          height="336"
          fit="cover"
          format="auto"
        />
      </div>
    </div>
    <div class="col opinion-container">
      <div class="line header">
        <div
          v-if="isMobile || isLargeMobile || isTabletPortrait || isSmallScreen"
        >
          <div class="img-container">
            <NuxtImg
              v-if="opinion.user?.avatar"
              :src="opinion.user.avatar.url"
              :alt="opinion.user.nickname"
              loading="lazy"
              quality="80"
              width="336"
              height="336"
              fit="cover"
              format="auto"
            />
          </div>
        </div>
        <div class="header-content">
          <div class="waper">
            <p class="name">
              {{ readMore(capitalize(opinion.user?.nickname), 25) }}
            </p>
            <p class="points">{{ opinion.user?.points_total }} points</p>
          </div>
          <div class="score-item">
            <ScoreResult
              :score="opinion.score"
              :dark="true"
            />
          </div>
        </div>
      </div>
      <div class="opinion-content">
        <h4>{{ opinion.title }}</h4>
        <p
          v-if="opinion.dates?.length"
          class="dates"
        >
          {{ opinion.dates.join(' · ') }}
        </p>
        <p
          class="description"
          v-html="userTextToHtml(opinion.description)"
        />
        <div
          v-if="opinion.medias?.length"
          class="medias"
        >
          <h5>Photos et vidéos du Waper</h5>
          <div class="medias-container">
            <div
              v-for="(media, index) in opinion.medias"
              :key="media.url_media"
              class="media-thumb"
              @click="mediaZoom = media"
            >
              <img
                v-if="media.type === 1"
                :src="media.url_media"
                :alt="`image #${index} de ${opinion.title}`"
                loading="lazy"
              />
              <img
                v-if="media.type === 2"
                :src="getThumbnail(media.url_media)"
                :alt="`image #${index} de ${opinion.title}`"
                loading="lazy"
              />
              <img
                v-if="media.type === 2"
                src="~assets/icons/youtube_dark.svg"
                alt="logo youtube"
                class="play"
              />
            </div>
          </div>
        </div>
        <div class="gauges">
          <div
            v-for="category in opinion.categories"
            :key="category.title"
            class="category"
          >
            <div class="text">
              <p class="title">{{ category.title }}</p>
              <p class="score">{{ numberToString(category.score) }}</p>
            </div>
            <div class="gauge">
              <div
                class="innerGauge"
                :style="{ width: `${(category.score / 5) * 100}%` }"
              />
            </div>
          </div>
        </div>
        <div
          v-if="opinion.recommendation"
          class="recommendation"
        >
          <div class="icon">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 11.189 9.739"
            >
              <path
                d="M3.183 9.466a.944.944 0 0 0 1.4-.075l6.4-7.851A.946.946 0 1 0 9.515.347L3.781 7.402 1.608 5.255A.944.944 0 0 0 .281 6.597l2.9 2.87"
                fill="#fff"
                fill-rule="evenodd"
              />
            </svg>
          </div>
          <p>Je recommande ce produit à mes amis</p>
        </div>
        <div
          v-else
          class="recommendation red"
        >
          <div class="icon">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 10.5 10.5"
            >
              <g fill="#fff">
                <path
                  d="M1.221.208A.713.713 0 1 0 .213 1.216l9.074 9.074a.713.713 0 1 0 1.008-1.008Z"
                />
                <path
                  d="M.208 9.283a.713.713 0 1 0 1.008 1.008l9.074-9.074A.713.713 0 1 0 9.282.209Z"
                />
              </g>
            </svg>
          </div>
          <p>Je ne recommande pas ce produit à mes amis</p>
        </div>
        <div class="answers">
          <div
            v-if="opinion.ps_version"
            class="answer-item answer-console"
          >
            <p class="label">Sur quelle console avez-vous joué à ce jeu ?</p>
            <p class="answer">
              <img
                :src="`/img/logo-${opinion.ps_version.toLowerCase()}.svg`"
                :alt="`logo ${opinion.ps_version.toUpperCase()}`"
                loading="lazy"
                width="92"
                height="20"
                class="svg"
              />
            </p>
          </div>
          <div
            v-for="answer in opinion.answers"
            :key="answer.answer"
            class="answer-item"
          >
            <p class="label">{{ answer.label }}</p>
            <p class="answer">{{ answer.answer }}</p>
          </div>
        </div>
        <div
          class="report"
          :class="{ 'is-reported': reported }"
          :aria-disabled="reported || undefined"
          :title="reported ? 'Vous avez déjà signalé cet avis' : undefined"
          @click.prevent="reportBV"
        >
          {{ reported ? 'Avis signalé' : 'Signaler ce post' }}
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { checkLoginModal } from '@/modals/checkLogin'
import { getReportModal } from '~/modals/report'
import type { Opinion } from '~/types'

const props = defineProps<{
  opinion: Opinion
}>()

const mediaZoom = ref<NonNullable<Opinion['medias']>[number]>()

const {
  isMobile,
  isLargeMobile,
  isTabletPortrait,
  isSmallScreen,
  isTabletLandscape,
  isDesktop,
} = useCustomMediaQuery()

const { authenticated } = useAuthenticated()
const reportModal = getReportModal({
  type: 'bv_review',
  slug: props.opinion.slug as string,
})

// already reported by this Waper: greyed out, nothing opens (one report per Waper and content)
const { isReported } = useMyWarns()
const reported = computed(() =>
  isReported('bv_review', props.opinion.slug as string),
)

const reportBV = () => {
  if (reported.value) {
    return
  }
  if (authenticated.value) {
    reportModal.open()
  } else {
    checkLoginModal.open()
  }
}

const getThumbnail = (url?: string) => {
  if (!url) return
  const info = extractVideoId(url)

  switch (info?.source) {
    case 'youtube':
      return `https://img.youtube.com/vi/${info.id}/maxresdefault.jpg`

    case 'dailymotion':
      return `https://www.dailymotion.com/thumbnail/video/${info.id}`

    case 'vimeo':
      {
        const { error } = useFetch(
          `http://vimeo.com/api/v2/video/${info.id}.json`,
          {
            query: {
              callback: 'showThumb',
            },
          },
        )

        if (error.value) {
          logError(error)
        }
      }
      break
    default:
      break
  }
}
</script>

<style lang="scss" scoped>
.waper-opinion-item {
  display: flex;
  width: 100%;
  color: $dark;
  text-decoration: none;
  cursor: pointer;

  .img-container {
    flex-shrink: 0;
    width: 8.4rem;
    height: 8.4rem;
    margin-right: 3rem;
    overflow: hidden;
    border-radius: 4.2rem;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  .opinion-container {
    flex-grow: 1;
    padding-top: 2rem;

    .header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;

      .header-content {
        display: flex;
        flex-grow: 1;
        justify-content: space-between;
        align-items: flex-start;
      }

      .waper {
        text-align: left;

        .name {
          margin-bottom: 0.2rem;
          font-size: 1.6rem;
          font-weight: bold;
          line-height: 2.1rem;
        }

        .points {
          font-size: 1.4rem;
          font-weight: 300;
          line-height: 1.8rem;
          opacity: 0.5;
        }
      }
    }

    .opinion-content {
      margin-top: 4rem;

      h4 {
        margin-bottom: 2.5rem;
        font-size: 1.8rem;
        font-weight: 500;
        line-height: 1.8rem;

        &:has(+ .dates) {
          margin-bottom: 0.8rem;
        }
      }

      .dates {
        margin-bottom: 2.5rem;
        font-size: 1.2rem;
        line-height: 1.6rem;
        color: $dark;
      }

      .description {
        margin-bottom: 2rem;
        white-space: pre-line;
      }

      .medias {
        margin-bottom: 2rem;

        h5 {
          margin-bottom: 0;
          font-size: 1.4rem;
          font-weight: 500;
        }

        .medias-container {
          display: flex;
          flex-wrap: wrap;

          .media-thumb {
            position: relative;
            width: 45%;
            margin-top: 1.5em;
            margin-right: 1.5em;
            cursor: pointer;

            img {
              width: 100%;
              height: 100%;
              object-fit: cover;
            }

            .play {
              position: absolute;
              top: 50%;
              left: 50%;
              width: 5.9rem;
              height: 4.1rem;
              transform: translate(-50%, -50%);
            }
          }
        }
      }

      .gauges {
        margin-bottom: 3rem;

        .category {
          width: 100%;
          margin-bottom: 1.2rem;

          .text {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 1.4rem;

            .title {
              font-size: 1.4rem;
              line-height: 1.8rem;
            }

            .score {
              font-size: 2.6rem;
              line-height: 3.4rem;
            }
          }

          .gauge {
            position: relative;
            width: 100%;
            height: 1rem;
            background: rgba(#ededed, 0.77);
            border-radius: 0.5rem;

            .innerGauge {
              position: absolute;
              top: 0;
              left: 0;
              height: 100%;
              background: rgb(21 163 216);
              background: linear-gradient(
                270deg,
                rgb(21 163 216 / 1) 0%,
                rgb(17 114 206 / 1) 100%
              );
              border-radius: 0.5rem;
            }
          }
        }
      }

      .recommendation {
        display: flex;
        align-items: center;
        margin-bottom: 2.7rem;

        &.red {
          .icon {
            background: $red;

            svg {
              width: 1.05rem;
              height: 1.05rem;
            }
          }
        }

        .icon {
          display: flex;
          justify-content: center;
          align-items: center;
          width: 3.5rem;
          height: 3.5rem;
          margin-right: 2rem;
          background: $blue;
          border-radius: 1.8rem;

          svg {
            width: 1.12rem;
            height: 0.974rem;
          }
        }

        p {
          font-size: 1.2rem;
          line-height: 1.6rem;
        }
      }

      .answers {
        margin-bottom: 4rem;
        border-bottom: 1px solid rgba($black, 0.2);

        .answer-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          height: 5.4rem;
          border-top: 1px solid rgba($black, 0.2);

          .label {
            font-size: 1.2rem;
            line-height: 1.6rem;
          }

          .answer {
            font-size: 1.2rem;
            font-weight: bold;
            line-height: 1.6rem;

            svg,
            .svg {
              width: 9.2rem;
              height: 2rem;

              @media screen and (max-width: 440px) {
                width: 7.1rem;
                height: 1.5rem;
              }
            }
          }
        }
      }

      .report {
        text-align: right;

        &:hover {
          color: $blue;
        }

        &.is-reported,
        &.is-reported:hover {
          color: #a8a8a8;
          cursor: default;
        }
      }
    }
  }
}

@media screen and (max-width: 700px) {
  .waper-opinion-item {
    .opinion-container {
      .header {
        justify-content: flex-start;
        align-items: center;

        .img-container {
          margin-right: 2rem;
        }

        .header-content {
          display: flex;
          flex-grow: 1;
          flex-wrap: wrap;
          justify-content: space-between;

          .score-item {
            margin-top: 1rem;
          }
        }

        .waper {
          flex-grow: 1;
          margin-right: 1rem;
        }
      }
    }
  }
}
</style>
